import React, { useState, useCallback } from 'react';
import { Box, Text, VStack, HStack, Flex } from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { Icon } from 'src/components/atoms/Icon';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { UploadedDataResponse, UploadProgress } from 'src/types';
import { captureException } from 'src/libs/sentry';
import { useAppContext } from 'src/contexts/AppContext';
import { parseMultipleExcelFiles } from 'src/utils/excelParser';

// Template file paths (files should be placed in public folder)
const marketingDataTemplatePath = '/Yapamar_Marketing_Data_Template.xlsx';
const kpiDataTemplatePath = '/Yapamar_KPI_Data_Upload_Template.xlsx';

// Alternative paths for development
const marketingDataTemplateDevPath =
  '/src/assets/data_template/Yapamar_Marketing_Data_Template.xlsx';
const kpiDataTemplateDevPath =
  '/src/assets/data_template/Yapamar_KPI_Data_Upload_Template.xlsx';

interface FileUploadBoxProps {
  onFilesSelected?: (files: File[]) => void;
  onAnalyze?: (files: File[], fileIds: string[]) => void;
  onPreviewChart?: (
    files: File[],
    previewData: UploadedDataResponse['data'],
  ) => void;
  customerId?: string;
}

interface FileUploadState {
  file: File;
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress?: UploadProgress;
  error?: string;
  notifications?: string;
}

const FileUploadBox: React.FC<FileUploadBoxProps> = ({
  onFilesSelected,
  onAnalyze: _onAnalyze,
  onPreviewChart,
  customerId: _customerId,
}) => {
  const { currentCustomer } = useAppContext();
  const { toastState, showError, hideToast } = useCustomToast();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadStates, setUploadStates] = useState<
    Map<string, FileUploadState>
  >(new Map());
  const [isDragOver, setIsDragOver] = useState(false);
  const [isParsing, setIsParsing] = useState(false);

  const hasCustomerAccess = !!currentCustomer;

  const validateFiles = (files: FileList): File[] => {
    const validFiles: File[] = [];

    Array.from(files).forEach((file) => {
      validFiles.push(file);
    });

    return validFiles;
  };

  const handleFileSelect = useCallback(
    (files: FileList) => {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        const remainingSlots = 2 - selectedFiles.length;
        const filesToAdd = validFiles.slice(0, remainingSlots);

        if (filesToAdd.length > 0) {
          setSelectedFiles((prev) => [...prev, ...filesToAdd]);
          onFilesSelected?.(filesToAdd);
        }
      }
    },
    [onFilesSelected, selectedFiles.length],
  );

  const handlePreviewChart = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    setIsParsing(true);

    try {
      // Parse Excel files locally
      const previewData = await parseMultipleExcelFiles(selectedFiles);

      // Call the preview callback with files and parsed data
      onPreviewChart?.(selectedFiles, previewData);
    } catch (error) {
      captureException({ error, fn: 'handlePreviewChart' });
      showError(
        'Error parsing files',
        'Failed to parse Excel files. Please check the file format.',
      );
    } finally {
      setIsParsing(false);
    }
  }, [selectedFiles, onPreviewChart, showError]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (selectedFiles.length < 2 && hasCustomerAccess) {
        setIsDragOver(true);
      }
    },
    [selectedFiles.length, hasCustomerAccess],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (selectedFiles.length < 2 && hasCustomerAccess) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect, selectedFiles.length, hasCustomerAccess],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && selectedFiles.length < 2 && hasCustomerAccess) {
        handleFileSelect(e.target.files);
      }
    },
    [handleFileSelect, selectedFiles.length, hasCustomerAccess],
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 bytes';
    const k = 1024;
    const sizes = ['bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Download template functions
  const downloadTemplate = async (
    primaryUrl: string,
    fallbackUrl: string,
    filename: string,
  ) => {
    const urls = [primaryUrl, fallbackUrl];

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          continue; // Try next URL
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the object URL
        window.URL.revokeObjectURL(downloadUrl);
        return; // Success, exit function
      } catch {
        continue; // Try next URL
      }
    }

    // If all URLs fail, try direct link approach
    const link = document.createElement('a');
    link.href = primaryUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadMarketingTemplate = () => {
    downloadTemplate(
      marketingDataTemplatePath,
      marketingDataTemplateDevPath,
      'Yapamar_Marketing_Data_Template.xlsx',
    );
  };

  const handleDownloadKpiTemplate = () => {
    downloadTemplate(
      kpiDataTemplatePath,
      kpiDataTemplateDevPath,
      'Yapamar KPI Data Template.xlsx',
    );
  };

  // Check if upload is disabled
  const isUploadDisabled =
    selectedFiles.length >= 2 || isParsing || !hasCustomerAccess;

  // Check if preview chart is disabled
  const isPreviewDisabled = isParsing || selectedFiles.length === 0;

  return (
    <Box w="100%">
      <Box
        w="100%"
        h="433px"
        bg={isDragOver ? 'border.lightGreen' : 'surface.sidebarMuted'}
        borderRadius="16px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="sm"
        border="1px solid"
        borderColor={
          isDragOver
            ? 'icon.green'
            : selectedFiles.length >= 2
              ? 'surface.border'
              : 'surface.border'
        }
        fontFamily="marketingPage"
        transition="all 0.2s"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        mb={6}
        opacity={isUploadDisabled ? 0.6 : 1}
        cursor={isUploadDisabled ? 'not-allowed' : 'default'}>
        <VStack spacing={3} w="100%" align="center">
          <Text
            fontWeight={600}
            fontSize="md"
            color="content.body"
            textAlign="center">
            {!hasCustomerAccess
              ? 'No customer access available'
              : selectedFiles.length >= 2
                ? 'Maximum files uploaded (2/2)'
                : `Drag and drop Files Here (${selectedFiles.length}/2)`}
          </Text>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              buttonName="Browse Files"
              size="lg"
              bg="icon.green"
              color="white"
              _hover={{ bg: 'icon.green', opacity: 0.9 }}
              fontFamily="marketingPage"
              borderRadius={8}
              px={9}
              py={3}
              onClick={() =>
                !isUploadDisabled &&
                document.getElementById('file-input')?.click()
              }
              isDisabled={isUploadDisabled}
              opacity={isUploadDisabled ? 0.6 : 1}>
              Browse Files
            </Button>
            <input
              id="file-input"
              type="file"
              multiple
              accept=".csv,.xlsx,.xls"
              onChange={handleFileInput}
              style={{ display: 'none' }}
              disabled={isUploadDisabled}
            />
          </Box>
          <Text color="content.muted" fontSize="sm" textAlign="center">
            {!hasCustomerAccess
              ? 'Please contact your administrator for customer access'
              : selectedFiles.length >= 2
                ? 'Maximum files reached'
                : 'Accepted formats: CSV, XLSX - Max 100 MB'}
          </Text>
        </VStack>
      </Box>

      {/* Template Download Links */}
      <Box mb={6}>
        <Text fontSize="sm" color="content.muted" textAlign="center" mb={3}>
          Need help formatting your data? Download our templates:
        </Text>
        <Flex
          direction={{ base: 'column', sm: 'row' }}
          justify="center"
          align="center"
          gap={3}>
          <Button
            buttonName="Download Marketing Template"
            size="sm"
            variant="outline"
            color="icon.green"
            borderColor="icon.green"
            fontFamily="marketingPage"
            borderRadius={6}
            px={4}
            py={2}
            onClick={handleDownloadMarketingTemplate}
            leftIcon={<Icon iconName="download" w="16px" h="16px" />}>
            Marketing Data Template
          </Button>
          <Button
            buttonName="Download KPI Template"
            size="sm"
            variant="outline"
            color="icon.green"
            borderColor="icon.green"
            fontFamily="marketingPage"
            borderRadius={6}
            px={4}
            py={2}
            onClick={handleDownloadKpiTemplate}
            leftIcon={<Icon iconName="download" w="16px" h="16px" />}>
            KPI Data Template
          </Button>
        </Flex>
      </Box>

      {/* File List and Analyze Button */}
      {selectedFiles.length > 0 && (
        <Box>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={4}>
            <Flex
              direction="row"
              wrap="wrap"
              gap={4}
              flex={1}
              justify={{ base: 'center', md: 'flex-start' }}>
              {selectedFiles.map((file, index) => {
                const uploadState = uploadStates.get(file.name);
                const status = uploadState?.status || 'idle';

                return (
                  <Box key={index} position="relative">
                    <Box
                      w={{ base: '100%', sm: '285px' }}
                      h="auto"
                      p={3}
                      bg="surface.panel"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor={
                        status === 'failed' ? 'border.red' : 'surface.border'
                      }
                      boxShadow="sm">
                      <HStack spacing={3} alignItems="center">
                        <Box
                          w="40px"
                          h="40px"
                          borderRadius="4px"
                          bg={'#41A5EE1A'}
                          display="flex"
                          alignItems={'center'}
                          justifyContent={'center'}>
                          <Icon iconName="excel" w="24px" h="24px" />
                        </Box>
                        <VStack spacing={0} align="flex-start" flex={1}>
                          <Text
                            fontSize="14px"
                            fontWeight={500}
                            color="content.body"
                            fontFamily="marketingPage"
                            noOfLines={1}>
                            {file.name}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="content.muted"
                            fontFamily="marketingPage">
                            {formatDate(new Date(file.lastModified))}{' '}
                            {formatFileSize(file.size)}
                          </Text>
                        </VStack>
                        <Button
                          buttonName="Delete File"
                          size="sm"
                          variant="ghost"
                          color="border.red"
                          p={1}
                          margin={'auto'}
                          minW="auto"
                          isDisabled={
                            status === 'uploading' || status === 'processing'
                          }
                          onClick={() => {
                            setSelectedFiles((prev) =>
                              prev.filter((_, i) => i !== index),
                            );
                            setUploadStates((prev) => {
                              const newMap = new Map(prev);
                              newMap.delete(file.name);
                              return newMap;
                            });
                          }}>
                          <Icon
                            iconName="trash"
                            w="20px"
                            h="20px"
                            fill={'white'}
                          />
                        </Button>
                      </HStack>
                    </Box>
                    {/* Error message positioned below the file box */}
                    {status === 'failed' && (
                      <Text
                        fontSize="sm"
                        color="border.red"
                        mt={2}
                        whiteSpace="nowrap">
                        {uploadState?.error || 'This file cannot be analyzed'}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </Flex>

            <Button
              buttonName="Preview Chart"
              size="lg"
              bg="icon.green"
              color="white"
              _hover={{ bg: 'icon.green', opacity: 0.9 }}
              margin={'auto'}
              fontFamily="marketingPage"
              borderRadius={8}
              px={8}
              py={3}
              w={{ base: '100%', md: 'auto' }}
              isLoading={isParsing}
              loadingText="Loading Preview..."
              onClick={handlePreviewChart}
              isDisabled={isPreviewDisabled}>
              {isParsing ? 'Loading Preview...' : 'Preview Chart'}
            </Button>
          </Flex>
        </Box>
      )}

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
    </Box>
  );
};

export default FileUploadBox;
